import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { BUSINESS_INFO } from '../../../mocks/lisaMerkleData';

export default function AboutIntroSection() {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        el.querySelectorAll('.reveal').forEach((node, i) => setTimeout(() => node.classList.add('revealed'), i * 100));
        obs.disconnect();
      }
    }, { threshold: 0.1 });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  return (
    <section ref={ref} className="bg-white py-28 overflow-hidden">
      <div className="max-w-[1400px] mx-auto px-8">
        <div className="grid lg:grid-cols-2 gap-0 border border-stone/20">
          {/* Image stack panel */}
          <div className="reveal relative min-h-[580px] overflow-hidden">
            <img
              src="https://public.readdy.ai/ai/img_res/75852761007d9c1bea6775527957f60d.jpg"
              alt="Lisa Merkle Design luxury interior project — Beverly Hills"
              className="w-full h-full object-cover object-top absolute inset-0 transition-transform duration-1000 hover:scale-105"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-transparent to-charcoal/20" />
            {/* Secondary image overlap */}
            <div className="absolute bottom-8 right-8 w-48 h-60 overflow-hidden border-4 border-white shadow-2xl">
              <img
                src="https://readdy.ai/api/search-image?query=Luxury%20interior%20design%20detail%20shot%20fine%20marble%20surfaces%20elegant%20decor%20accessories%20warm%20neutral%20tones%20Beverly%20Hills%20high%20end%20residential%20design%20refined%20editorial%20aesthetic%20close%20up%20artistic%20composition&width=300&height=400&seq=about-secondary1&orientation=portrait"
                alt="Lisa Merkle Design luxury detail"
                className="w-full h-full object-cover object-top"
              />
            </div>
            {/* Years badge */}
            <div className="absolute top-8 left-8 bg-charcoal/90 px-6 py-4 backdrop-blur-sm">
              <p className="font-display text-cream text-4xl font-light leading-none">20+</p>
              <p className="text-stone/70 text-[10px] tracking-widest uppercase font-sans mt-1">Years of Excellence</p>
            </div>
          </div>

          {/* Content panel */}
          <div className="reveal reveal-delay-2 flex flex-col justify-center p-12 lg:p-16 bg-cream-warm">
            <span className="text-[10px] tracking-[0.28em] uppercase text-stone font-sans">Welcome to Our World</span>
            <h2 className="font-display text-4xl md:text-5xl font-light text-charcoal mt-4 mb-6 leading-tight">
              World of Style,<br />Comfort &{' '}
              <em className="not-italic text-stone-dark">Elevated Living</em>
            </h2>
            <div className="w-10 h-px bg-stone mb-8" />

            <div className="flex flex-col gap-4">
              <p className="text-charcoal/60 text-sm leading-relaxed font-sans">
                Lisa Merkle Design offers curated modern classic luxury remodel and interior design services in Beverly Hills, Los Angeles, and across the United States. With a trusted network of top-tier vendors, craftsmen, and trades, we create tailored interiors that reflect your taste, your space, and your lifestyle.
              </p>
              <p className="text-charcoal/60 text-sm leading-relaxed font-sans">
                Lisa is honorably trained at <strong className="text-charcoal font-medium">UCLA</strong> and shaped by years of experience at the best celebrity design firms in Los Angeles. Her formal training encompasses classical French, Italian, and Spanish design, antiquities, and construction details.
              </p>
              <p className="text-charcoal/60 text-sm leading-relaxed font-sans">
                Our work has been featured in <strong className="text-charcoal font-medium">Elle Décor</strong> and <strong className="text-charcoal font-medium">Architectural Digest</strong> — reflecting the studio's position at the top tier of luxury residential design.
              </p>
            </div>

            <div className="flex flex-wrap gap-3 mt-10">
              <Link to="/about" className="px-7 py-3 bg-charcoal text-cream text-[10px] tracking-[0.2em] uppercase font-medium font-sans rounded-full hover:bg-charcoal/80 transition-all cursor-pointer whitespace-nowrap">
                Meet Lisa Merkle
              </Link>
              <a href={BUSINESS_INFO.phoneHref} className="px-7 py-3 border border-charcoal/20 text-charcoal text-[10px] tracking-[0.2em] uppercase font-medium font-sans rounded-full hover:bg-charcoal/5 transition-all cursor-pointer whitespace-nowrap">
                {BUSINESS_INFO.phone}
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
